import { useState } from 'react';
import { transitRoutes, fares } from '../../../mocks/transportationPage';

export default function TransitSection() {
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);

  // Safeguard: ensure transitRoutes and fares are arrays before rendering
  if (!Array.isArray(transitRoutes) || !Array.isArray(fares)) {
    console.error('Transit data is missing or malformed.');
    return null;
  }

  return (
    <section id="transit" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-100 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
            Public Transit
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Roseville Transit Routes</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Roseville Transit operates 6 routes connecting neighborhoods, shopping centers, employment hubs,
            and Sacramento commuter services.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {transitRoutes.map((route) => (
            <div
              key={route.id}
              onClick={() =>
                setSelectedRoute(selectedRoute === route.id ? null : route.id)
              }
              className={`bg-white rounded-xl p-5 shadow-sm border-2 transition-all cursor-pointer ${
                selectedRoute === route.id
                  ? 'border-teal-500 shadow-lg'
                  : 'border-gray-100 hover:border-teal-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: route.color }}
                  >
                    {route.name.split(' ')[1]}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{route.name}</h4>
                    <span className="text-xs text-gray-500">{route.type}</span>
                  </div>
                </div>
                <i
                  className={`ri-arrow-${
                    selectedRoute === route.id ? 'up' : 'down'
                  }-s-line text-gray-400`}
                ></i>
              </div>

              <p className="text-gray-600 text-sm mb-3">{route.description}</p>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="flex items-center space-x-1">
                  <i className="ri-time-line"></i>
                  <span>{route.frequency}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <i className="ri-calendar-line"></i>
                  <span>{route.hours}</span>
                </span>
              </div>

              {selectedRoute === route.id && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Route Stops:</p>
                  <div className="flex flex-wrap gap-2">
                    {route.stops.map((stop, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                      >
                        <span
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: route.color }}
                        ></span>
                        {stop}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fares */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4 flex items-center space-x-2">
                <i className="ri-ticket-2-line"></i>
                <span>Transit Fares</span>
              </h3>
              <p className="text-teal-100 mb-6 text-sm">
                Affordable fares for everyone. Purchase passes at the Transit Center or through the Roseville
                Transit app.
              </p>
              <a
                href="https://www.roseville.ca.us/government/departments/public_works/roseville_transit"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Plan Your Trip</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {fares.map((fare, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-teal-100 text-xs mb-1">{fare.type}</p>
                  <p className="text-2xl font-bold">{fare.price}</p>
                  <p className="text-teal-200 text-xs">{fare.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
